/*
 * WHICH LISTS THIS VIEW IS SHOWING, and the control that changes it.
 *
 * The shared picker of multi-list build item 3, on Active, Upcoming and Past.
 * Decision 473's disclosure pattern, which the temporal tabs already use: a
 * button whose label STATES the current answer, a panel that three things close,
 * and no second mechanism invented for the second use of it.
 *
 * ONE PLACE IT DIFFERS FROM 473, and it is the reason this is not simply that
 * component with different children: the temporal panel holds NavLinks, so it is
 * navigation and principle 1 forbids a `<select>` there. This panel holds
 * CHECKBOXES that write a preference, one per list, over a "Select all" button.
 * Nothing here is an anchor and nothing touches history — but it is still not a
 * native `<select multiple>`, which is unusable on a phone (plan §6.2), and
 * still not the single-select `<select>` Manage fencers uses, which answers
 * "which list am I EDITING" — a different question with exactly one answer.
 *
 * THE LABEL STATES THE SCOPE, and the client writes every sentence (416): "All
 * fencers" over every list, a list's own NAME whenever exactly one is in scope,
 * and "2 of 3 lists" for anything between. A button reading "Lists" would spend
 * the label to buy nothing, which is 473's argument about a disclosure reading
 * "Sections". See the label's own comment below for why the count is the last
 * of the three rather than the general case.
 *
 * TICKING EVERY LIST WRITES `all`, NOT A SET OF ALL OF THEM. "All lists" and
 * "the four lists I ticked" are different answers — the first includes a list
 * created tomorrow (502) — which is why the mode travels beside the ids and why
 * this control writes the mode rather than always writing a set.
 *
 * DESELECTING THE LAST LIST IS REFUSED IN PLACE. The panel stays open, nothing
 * is written, and the reader is told why. The server refuses `selected` with an
 * empty set (422), but a client that relied on that would be asking the server
 * to write the sentence, and there is no scope for it to answer with.
 *
 * PURE OVER ITS PROPS except for the route it reads to close itself. The scope
 * and the lists are passed in because the ROUTE needs them too — Active's
 * scope-empty state is gated on the scope being narrower than all lists — and
 * one read shared beats two that can disagree.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import type { ViewListScope } from "../lib/api";
import type { ListRow } from "../lib/useMultiList";
import type { ScopeChoice } from "../lib/useViewScope";
import styles from "./ListScope.module.css";

export interface ListScopeProps {
  /** Every list the account holds, in the server's order (primary first). */
  lists: ListRow[];
  /** The stored mode. */
  scope: ViewListScope;
  /** What the mode resolves to right now — never empty (502's fallback). */
  listIDs: string[];
  /** A write is in flight. */
  saving?: boolean;
  /** The last write did not land. */
  failed?: boolean;
  onChange: (next: ScopeChoice) => void;
  /**
   * Active only: how many people on each list are still fencing today, by list
   * id. A list with a count reads "My fencers (2 active)" in the panel; one
   * without reads its bare name. Upcoming and Past pass nothing (Kevin's review).
   */
  activeCounts?: ReadonlyMap<string, number>;
}

export function ListScope({
  lists,
  scope,
  listIDs,
  saving = false,
  failed = false,
  onChange,
  activeCounts,
}: ListScopeProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [refused, setRefused] = useState(false);
  const panelID = useId();
  const wrap = useRef<HTMLDivElement>(null);

  // A route change closes it (473). Keyed on the pathname rather than on a
  // press, so a back or forward while the panel is open closes it too.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // `pointerdown` and not `click`, for 473's reason: a click fires late enough
    // on a touch device to see the panel sit open under the finger.
    const onPointer = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  // Closing it forgets the refusal: the sentence answers a press, and a reader
  // reopening the panel has not made that press yet.
  useEffect(() => {
    if (!open) setRefused(false);
  }, [open]);

  /*
   * THE PANEL IS PLACED BY MEASURING IT, not by one fixed edge (Kevin's
   * review). The stylesheet opens it from the button's inline start, which was
   * measured right for Upcoming and Past, where the button sits near the start
   * of its row. On Active the button sits further toward the end, and the
   * "(2 active)" rows widen the panel, so the same rule opened it off the right
   * edge of a phone. Measured before paint, so it never flashes in the wrong
   * place; re-measured on resize and whenever the rows change width.
   */
  const panel = useRef<HTMLDivElement>(null);
  const [panelLeft, setPanelLeft] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      setPanelLeft(null);
      return;
    }
    const place = () => setPanelLeft(panelOffset(wrap.current, panel.current));
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, lists, activeCounts, refused, failed]);

  const selected = new Set(listIDs);
  const everyList = lists.length > 0 && lists.every((l) => selected.has(l.id));

  /*
   * THE LABEL, FROM THE RESOLVED COUNT RATHER THAN FROM THE MODE (Kevin's rule,
   * 2026-09-15). Three cases, and they are total:
   *
   *   every list      -> "All fencers"
   *   exactly one     -> that list's NAME, whether the mode got there by being
   *                      `primary` or by a reader ticking one box. What the
   *                      reader wants to know is whose fencers these are, and
   *                      "1 of 3 lists" withholds the answer it has.
   *   anything else    -> "2 of 3 lists", which by arithmetic only happens above
   *                      two lists, since at two lists a subset is one list and
   *                      names itself.
   *
   * The count form needs no plural rules: it is reachable only at three lists or
   * more, so the total is never singular.
   */
  const soleName = listIDs.length === 1 ? soleListName(lists, listIDs, scope) : undefined;
  const label = everyList
    ? t("listScope.all")
    : (soleName ??
      t("listScope.selected", { selected: listIDs.length, total: lists.length }));

  /*
   * A LIST NAME IS CAPPED FOR DISPLAY, because nothing caps what a reader may
   * type into one (Kevin, 2026-09-15). The other two labels have a length this
   * component chose; a name does not, and an uncapped one drags the button
   * across the heading row and pushes the group-by toggle off a phone.
   *
   * Only the visible text is cut. The button's accessible name keeps the WHOLE
   * name, so a screen reader and a voice-control user get the real thing, and
   * the untruncated name is one press away in the panel — which is why cutting
   * it here costs nothing.
   */
  const shown = soleName ? truncateName(soleName) : label;

  const write = (next: ScopeChoice) => {
    setRefused(false);
    onChange(next);
  };

  /**
   * One list's checkbox.
   *
   * EVERY LIST TICKED WRITES `all` rather than a set naming them, which is the
   * mode/set distinction above: a reader who lands on `primary` and ticks the
   * rest has said "all of them", and storing the ids would quietly exclude the
   * next list they make.
   */
  const toggleList = (id: string, checked: boolean) => {
    if (saving) return;
    // Built by walking `lists` rather than by appending, so the set written is
    // always in the server's own order.
    const next = checked
      ? lists.filter((l) => selected.has(l.id) || l.id === id).map((l) => l.id)
      : listIDs.filter((held) => held !== id);

    if (next.length === 0) {
      setRefused(true);
      return;
    }
    write(next.length === lists.length ? { scope: "all" } : { scope: "selected", listIDs: next });
  };


  return (
    <div className={styles.wrap} ref={wrap}>
      <button
        type="button"
        className={styles.button}
        // 321: a real accessible name from aria-label, not a hidden span. It
        // CONTAINS the visible label, so a voice-control user can say what they
        // can see.
        aria-label={t("listScope.button", { scope: label })}
        aria-expanded={open}
        aria-controls={panelID}
        onClick={() => setOpen((v) => !v)}
      >
        {/* The label and the chevron are ONE CENTRED PAIR (Kevin, 2026-09-15):
            on a fixed-width control the slack has to open outside them, not
            between them. The stylesheet records the two arrangements this
            replaced and what each looked like. */}
        <span className={styles.label}>{shown}</span>
        {/* Drawn inline, as the shell's chevron is: no font guarantees one.
            aria-hidden because the button is already named and its state is
            already carried by aria-expanded. */}
        <svg
          className={styles.chevron}
          viewBox="0 0 24 24"
          width="16"
          height="16"
          aria-hidden="true"
          focusable="false"
        >
          <path fill="none" stroke="currentColor" strokeWidth="2" d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {/* Mounted only when open, on 473's call: a display:none subtree is
          skipped by screen readers anyway, so keeping it would buy nothing and
          would put a second copy of every list in the DOM. */}
      {open ? (
        <div
          id={panelID}
          ref={panel}
          className={styles.panel}
          // Physical `left` with `right: auto` overrides the stylesheet's
          // logical inline-start in either direction; null keeps the
          // stylesheet's placement, which is also what a test without layout gets.
          style={panelLeft === null ? undefined : { left: `${panelLeft}px`, right: "auto" }}
          role="group"
          aria-label={t("listScope.panel")}
          aria-busy={saving || undefined}
        >
          {lists.map((l) => {
            // The panel rows only; the button's label stays as it was (Kevin's
            // review).
            const active = activeCounts?.get(l.id) ?? 0;
            return (
              <ScopeCheckbox
                key={l.id}
                label={
                  active > 0 ? t("listScope.activeCount", { name: l.name, count: active }) : l.name
                }
                checked={selected.has(l.id)}
                onChange={(checked) => toggleList(l.id, checked)}
              />
            );
          })}
          {/*
            "SELECT ALL" IS A BUTTON, NOT A CHECKBOX (Kevin, 2026-09-15). It was
            an "All lists" checkbox, and a checkbox promises it can be UNticked —
            which this one could not, because deselecting every list is the one
            thing the control refuses. A checkbox that answers a press with a
            refusal is lying about its own affordance; a button that does one
            thing is not.
            AND IT IS ABSENT ONCE EVERY LIST IS IN SCOPE, rather than disabled:
            pressing it would then do nothing, which is the control-with-no-
            outcome decision 418 rules out and 509 already applied to "Make
            primary" on the primary list. The rule beside it goes with it.

            IT SITS BELOW THE ROWS BECAUSE IT IS THE ONE ROW THAT COMES AND GOES
            (520, Kevin's fix). Above them, its appearance and disappearance
            moved every list item a row's height — while the reader's finger was
            on one of those items, since ticking the second-to-last list is what
            makes it vanish. Below them nothing above it can move: the panel
            grows and shrinks at its bottom edge, which is the edge no press is
            aimed at. It is also the weaker read of the two orders and worth
            saying so: an action ahead of the rows announces itself, where one
            after them is a fallback you find having looked. A control that
            shifts its own targets is the worse fault.
          */}
          {everyList ? null : (
            <>
              <div className={styles.rule} />
              <button
                type="button"
                className={styles.selectAll}
                onClick={() => write({ scope: "all" })}
              >
                {t("listScope.selectAll")}
              </button>
            </>
          )}
          {/*
            The refusal and the failed write are different sentences and only one
            can be true at a time: one is this control declining to write, the
            other is a write that did not land.
          */}
          {refused ? (
            <p className={styles.note} role="status">
              {t("listScope.lastList")}
            </p>
          ) : failed ? (
            <p className={styles.note} role="status">
              {t("error.couldNotSave")}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One row of the panel. At MODULE scope (492): a component declared inside a
 * render is a new type on every render and remounts its subtree, which for a
 * checkbox means losing focus mid-keyboard-use.
 */
function ScopeCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={styles.row}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/** The page's own side gutter on a phone, which the panel must stay inside. */
const PANEL_GUTTER = 16;

/**
 * Where the open panel's left edge goes, in px relative to the wrapper, or null
 * to keep the stylesheet's placement.
 *
 * Three candidates, in order, and the first that fits inside the viewport less
 * the gutter wins: aligned to the button's inline START (the stylesheet's own
 * placement, so a panel that already fits does not move), then its inline END,
 * then clamped into the viewport. Computed in PHYSICAL pixels with the
 * direction read off the element, so Arabic's right-to-left layout gets the
 * mirrored candidates rather than a second rule.
 *
 * Null when there is no layout to measure (a test environment) or the start
 * edge already fits, so the common case writes no inline style at all.
 */
export function panelOffset(
  wrap: HTMLElement | null,
  panel: HTMLElement | null,
): number | null {
  if (!wrap || !panel) return null;
  const viewport = document.documentElement.clientWidth || window.innerWidth;
  const width = panel.offsetWidth;
  if (!viewport || !width) return null;

  const button = wrap.getBoundingClientRect();
  const rtl = getComputedStyle(wrap).direction === "rtl";
  const min = PANEL_GUTTER;
  const max = viewport - PANEL_GUTTER - width;
  const fits = (left: number) => left >= min && left <= max;

  const start = rtl ? button.right - width : button.left;
  if (fits(start)) return null;
  const end = rtl ? button.left : button.right - width;
  const left = fits(end) ? end : Math.max(min, Math.min(end, max));
  return left - button.left;
}

/**
 * How much of a list's name the button shows.
 *
 * 18 characters, which fits every ordinary list name a coach types ("Saturday
 * squad", "Wednesday beginners") and cuts the ones that are really a sentence.
 * A CHARACTER cap rather than a CSS width, so the limit is the same on every
 * platform and can be asserted in a test; the stylesheet's own ellipsis is the
 * floor beneath it for 18 unusually wide glyphs.
 */
const NAME_MAX = 18;

function truncateName(name: string): string {
  if (name.length <= NAME_MAX) return name;
  // The ellipsis replaces a character rather than being added beyond the cap,
  // so the visible string never exceeds it.
  return `${name.slice(0, NAME_MAX - 1).trimEnd()}…`;
}

/**
 * The name of the one list in scope.
 *
 * FROM THE RESOLVED ID FIRST, falling back to the `is_primary` flag under the
 * `primary` mode: the resolved set is what the view is actually reading, so
 * naming from it cannot disagree with the rows on screen, and the flag is only
 * a second answer to the same question. Undefined if neither names a list the
 * caller holds, which the count form then covers — "1 of 3 lists" is true
 * whatever went missing.
 *
 * Displayed as stored (plan §6.4): the onboarding list's name is the reader's
 * own and is not re-cased or re-worded here.
 */
function soleListName(
  lists: ListRow[],
  listIDs: string[],
  scope: ViewListScope,
): string | undefined {
  const named =
    lists.find((l) => l.id === listIDs[0]) ??
    (scope === "primary" ? lists.find((l) => l.is_primary) : undefined);
  return named?.name;
}
